import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { User, ShoppingBag, Heart, X } from 'lucide-react';
import pb from '../lib/pocketbase';
import AuthModal from './AuthModal';
import InstallPrompt from './InstallPrompt';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useSettings } from '../context/SettingsContext';
import './Navbar.css';

const Navbar = () => {
  const { settings } = useSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(pb.authStore.model);
  const { cartCount, setIsCartOpen } = useCart();
  const { favorites, setIsFavoritesOpen } = useFavorites();
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  const location = useLocation();
  const isHome = location.pathname === '/';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    const handleClickOutside = (event) => {
      const inDesktop = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inMobile = mobileDropdownRef.current && mobileDropdownRef.current.contains(event.target);
      if (!inDesktop && !inMobile) {
        setDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    // Initial check
    handleScroll();

    // Auth state change listener
    const unsubscribe = pb.authStore.onChange((token, model) => {
      setUser(model);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      unsubscribe();
    };
  }, []);

  const handleLogout = () => {
    pb.authStore.clear();
    setDropdownOpen(false);
  };

  const handleOpenAuth = () => {
    setDropdownOpen(false);
    setIsAuthModalOpen(true);
  };

  const renderAnnouncement = () => {
    if (settings.isVacation) {
      return (
        <div className="announcement-bar vacation">
          ⚠️ {settings.vacationMessage || "Notre établissement est actuellement fermé pour congés."}
        </div>
      );
    }
    if (settings.specialAlert) {
      return (
        <div className="announcement-bar special">
          ✨ {settings.specialAlert}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <nav className={`navbar ${scrolled || !isHome ? 'scrolled' : ''}`}>
        {renderAnnouncement()}
        <div className="container navbar-container">
          <div className="logo-container">
            <Link to="/" className="logo-text">
              <img src={settings.siteLogo || "/images/logo_real.jpg"} alt={settings.siteName} className="logo-img" />
            </Link>
          </div>

          <div className="nav-links-container">
            <NavLink to="/" className="nav-link">Accueil</NavLink>
            <NavLink to="/gateaux" className="nav-link">Nos Gâteaux</NavLink>
            <NavLink to="/notre-carte" className="nav-link">Notre Carte</NavLink>
            <NavLink to="/entreprises" className="nav-link">Nos Pâtissiers</NavLink>
            <NavLink to="/nos-creations" className="nav-link">CRÉATIONS</NavLink>
            <NavLink to="/contact" className="nav-link">CONTACT</NavLink>
          </div>

          <div className="desktop-actions">
            {/* Favorites Icon */}
            <button
              className="user-icon-btn"
              onClick={() => setIsFavoritesOpen(true)}
              style={{ position: 'relative' }}
              aria-label="Favoris"
            >
              <Heart size={22} />
              {favorites.length > 0 && (
                <span className="cart-badge" style={{ backgroundColor: 'var(--color-gold)', right: '-8px' }}>
                  {favorites.length}
                </span>
              )}
            </button>

            {/* Cart Icon */}
            {!settings.isClickCollectDisabled && (
              <button
                className="user-icon-btn"
                onClick={() => setIsCartOpen(true)}
                style={{ position: 'relative' }}
                aria-label="Panier"
              >
                <ShoppingBag size={22} />
                {cartCount > 0 && (
                  <span className="cart-badge">{cartCount}</span>
                )}
              </button>
            )}

            {/* User Account Dropdown */}
            <div className="user-menu-container" ref={dropdownRef}>
              <button
                className="user-icon-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-label="Mon compte"
                style={user ? { color: 'var(--color-gold)' } : {}}
              >
                <User size={22} />
              </button>

              {dropdownOpen && (
                <div className="dropdown-menu">
                  {user ? (
                    <>
                      <div className="dropdown-item" style={{ cursor: 'default', fontWeight: 'bold', color: 'var(--color-brown)' }}>
                        {user.name || user.email}
                      </div>

                      {user.role === 'admin' && (
                        <Link to="/admin" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                          Tableau de bord
                        </Link>
                      )}

                      <Link to="/mon-compte" className="dropdown-item" onClick={() => setDropdownOpen(false)}>Mon Compte</Link>
                      <button className="dropdown-item" onClick={handleLogout} style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}>
                        Se déconnecter
                      </button>
                    </>
                  ) : (
                    <button className="dropdown-item" onClick={handleOpenAuth} style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}>
                      Se connecter / S'inscrire
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mobile-actions">
            {/* Mobile Favorites Icon */}
            <button
              className="user-icon-btn"
              onClick={() => setIsFavoritesOpen(true)}
              style={{ position: 'relative' }}
              aria-label="Favoris"
            >
              <Heart size={22} />
              {favorites.length > 0 && (
                <span className="cart-badge" style={{ backgroundColor: 'var(--color-gold)', right: '-8px' }}>
                  {favorites.length}
                </span>
              )}
            </button>

            {/* Mobile Cart Icon */}
            {!settings.isClickCollectDisabled && (
              <button
                className="user-icon-btn"
                onClick={() => setIsCartOpen(true)}
                style={{ position: 'relative' }}
                aria-label="Panier"
              >
                <ShoppingBag size={22} />
                {cartCount > 0 && (
                  <span className="cart-badge">{cartCount}</span>
                )}
              </button>
            )}

            {/* Mobile User Icon - Same dropdown as desktop */}
            <div className="user-menu-container" ref={mobileDropdownRef} style={{ position: 'relative' }}>
              <button
                className="user-icon-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-label="Mon compte"
                style={user ? { color: 'var(--color-gold)' } : {}}
              >
                <User size={22} />
              </button>

              {dropdownOpen && (
                <div className="dropdown-menu" style={{ right: 0, left: 'auto' }}>
                  {user ? (
                    <>
                      <div className="dropdown-item" style={{ cursor: 'default', fontWeight: 'bold', color: 'var(--color-brown)' }}>
                        {user.name || user.email}
                      </div>

                      {user.role === 'admin' && (
                        <Link to="/admin" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                          Tableau de bord
                        </Link>
                      )}

                      <Link to="/mon-compte" className="dropdown-item" onClick={() => setDropdownOpen(false)}>Mon Compte</Link>
                      <button className="dropdown-item" onClick={handleLogout} style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}>
                        Se déconnecter
                      </button>
                    </>
                  ) : (
                    <button className="dropdown-item" onClick={handleOpenAuth} style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}>
                      Se connecter / S'inscrire
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mobile-menu-btn" onClick={() => setIsOpen(!isOpen)}>
            <span className="hamburger"></span>
            <span className="hamburger"></span>
            <span className="hamburger"></span>
          </div>
        </div>

        {/* Mobile Sidebar Menu */}
        <div
          className={`mobile-sidebar-overlay ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(false)}
        ></div>

        <div className={`mobile-sidebar ${isOpen ? 'open' : ''}`}>
          <div className="mobile-sidebar-header">
            <div className="mobile-logo">
              <img src={settings.siteLogo || "/images/logo_real.jpg"} alt={settings.siteName} height="40" style={{ borderRadius: '50%', objectFit: 'cover' }} />
            </div>
            <button className="close-menu-btn" onClick={() => setIsOpen(false)}>
              <X size={24} />
            </button>
          </div>

          <div className="mobile-nav-links">
            <NavLink to="/" className="mobile-link" onClick={() => setIsOpen(false)}>Accueil</NavLink>
            <NavLink to="/gateaux" className="mobile-link" onClick={() => setIsOpen(false)}>Nos Gâteaux</NavLink>
            <NavLink to="/notre-carte" className="mobile-link" onClick={() => setIsOpen(false)}>Notre Carte</NavLink>
            <NavLink to="/entreprises" className="mobile-link" onClick={() => setIsOpen(false)}>Nos Pâtissiers</NavLink>
            <NavLink to="/nos-creations" className="mobile-link" onClick={() => setIsOpen(false)}>CRÉATIONS</NavLink>
            <NavLink to="/contact" className="mobile-link" onClick={() => setIsOpen(false)}>CONTACT</NavLink>

            <div className="mobile-divider"></div>

            {user ? (
              <>
                <NavLink to="/mon-compte" className="mobile-link" onClick={() => setIsOpen(false)}>
                  <User size={20} style={{ marginRight: '10px' }} />
                  {user.name || user.email}
                </NavLink>
                {user.role === 'admin' && (
                  <NavLink
                    to="/admin"
                    className="mobile-link"
                    onClick={() => setIsOpen(false)}
                    style={{ color: 'var(--color-gold)', borderLeft: '3px solid var(--color-gold)', paddingLeft: '17px' }}
                  >
                    <span style={{ fontSize: '1.2rem', marginRight: '10px' }}>⚙️</span>
                    Espace Admin
                  </NavLink>
                )}
              </>
            ) : (
              <button
                className="mobile-link account-btn"
                onClick={() => { setIsOpen(false); setIsAuthModalOpen(true); }}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none' }}
              >
                <User size={20} style={{ marginRight: '10px' }} />
                Mon Compte
              </button>
            )}

            <div className="mobile-divider"></div>
            <div style={{ padding: '0 20px', marginTop: '-10px' }}>
              <InstallPrompt className="install-pwa-btn--sidebar" />
            </div>
          </div>

          <div className="mobile-sidebar-footer">
            <p>© {new Date().getFullYear()} {settings.siteName}</p>
          </div>
        </div>
      </nav>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </>
  );
};

export default Navbar;
